import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../cn";

export type StatusBannerTone = "info" | "positive" | "warning" | "critical";

const FRAME: Record<StatusBannerTone, string> = {
  info: "border-info/40 bg-info-soft/50",
  positive: "border-positive/40 bg-positive-soft/50",
  warning: "border-warning/45 bg-warning-soft/55",
  critical: "border-critical/45 bg-critical-soft/55",
};

const MARK: Record<StatusBannerTone, string> = {
  info: "bg-info-soft text-info-text",
  positive: "bg-positive-soft text-positive-text",
  warning: "bg-warning-soft text-warning-text",
  critical: "bg-critical-soft text-critical-text",
};

export interface StatusBannerProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  tone: StatusBannerTone;
  /** A 20px stroke icon. The tone is also carried by the words, never the icon alone. */
  icon: ReactNode;
  /** What is true, in a few words. */
  title: ReactNode;
  /** One short line under the title. */
  children?: ReactNode;
  /** At most one control: the thing that fixes what the banner says. */
  action?: ReactNode;
  /**
   * Announce the banner when it appears. Only for one that arrives after
   * something the person did (a payment refused, a form that will not save);
   * a banner that is simply on the page when it loads is read with the page.
   */
  live?: "polite" | "assertive";
}

/**
 * One status, one line, one fix.
 *
 * The billing screens had a callout per state, each with its own paragraph; a
 * reader scanning for "is anything wrong" had to read all of them. This keeps
 * every state to the same shape: a marked icon, a title, a sentence and at
 * most one action, so the urgent ones are found by colour and the words say why.
 * Silent to a screen reader unless `live` says otherwise: seven banners
 * shouting on load is how a person learns to ignore the one that matters.
 */
export function StatusBanner({
  tone,
  icon,
  title,
  children,
  action,
  live,
  className,
  ...props
}: StatusBannerProps) {
  return (
    <div
      role={live === undefined ? undefined : live === "assertive" ? "alert" : "status"}
      className={cn(
        "flex flex-wrap items-center gap-x-3.5 gap-y-3 rounded-[var(--radius-card)] border px-4 py-3.5",
        FRAME[tone],
        className,
      )}
      {...props}
    >
      <span
        aria-hidden="true"
        className={cn(
          "grid size-9 shrink-0 place-items-center rounded-[var(--radius-lg)]",
          MARK[tone],
        )}
      >
        {icon}
      </span>
      <div className="flex min-w-0 flex-1 basis-56 flex-col gap-0.5">
        <p className="text-prose font-semibold text-ink">{title}</p>
        {children ? <div className="text-ui text-ink-2">{children}</div> : null}
      </div>
      {action ? <div className="flex shrink-0 items-center gap-2">{action}</div> : null}
    </div>
  );
}
